/**
 * Ambient lines, pushed by the clock (see hooks/useGameUpdate) rather than by an action, so
 * the log keeps moving while the player sits still. A Dark Room's log works the same way:
 * the world is the narrator, and it does not wait to be asked.
 *
 * Two places, because the clock runs in two places. The road lines report the country the
 * player is crossing; the town lines report the settlement they are sitting in. Nothing here
 * reports the player — walk's own messages do that ("legs trudge on"), and the two must stay
 * disjoint or the log reads like an echo.
 *
 * The town lines must also stay clear of lib/strings/city.js, which describes a town at the
 * moment of arrival. These are the hours afterwards, when nobody is looking at you.
 *
 * The light turning over used to be a narrator of its own (model/sky.js), on its own channel,
 * writing at the phase boundary the instant the flip registered. It was folded in here because
 * that channel was ungated: a phase is half a day and both calendar.nextStep and advancePhase
 * are a straight day/night flip, so *every* phase change crossed the light and the sky spoke on
 * every one of them — in a town, on top of the action the player had just taken. It was never
 * the rare narrator its header claimed; it ran at exactly ambient's rate and ignored ambient's
 * gates.
 *
 * So the light is no longer a separate voice. Its lines are simply what the world says as a
 * phase comes on, sorted into the pool for the light being arrived at — dusk lines belong to
 * night, dawn lines to day — and they are drawn, gated and silenced like any other line. The
 * cost is that a crossover is no longer explained at the moment it happens; the page fade and
 * the header icon carry it alone. See the town-cadence note in hooks/useGameUpdate.
 */

const dayLines = [
  'the sun beats down',
  'dust whirls up',
  'heat comes off the hardpan in sheets',
  'the light is flat and white',
  'wind out of the west and then nothing',
  'grit in the teeth',
  'the land goes on and does not change',
  // the light coming up, out where there is a horizon to watch it come up over
  'grey in the east',
  'shapes stand out of the murk'
];

const nightLines = [
  'the cold comes down',
  'the dark is absolute',
  'the wind moves low through the scrub',
  'stars cold and far and without number',
  'no moon. the road goes grey',
  // the light going
  'the light goes long',
  'shadows run out east and join',
  'the sun is gone behind the rim'
];

const townDayLines = [
  'the boards creak in the heat',
  'a shutter bangs somewhere and stops',
  'flies work at something in the road',
  'a horse shifts its weight and settles',
  'somewhere a hammer, and then nothing',
  'the trough is low and green at the edges',
  'the dark thins',
  'first light, and no warmth in it'
];

const townNightLines = [
  'lamps go out one by one',
  'a dog barks and is quiet',
  'boots on the boards. then gone',
  'the cold comes up through the floor',
  'a light stays on somewhere and burns down',
  'the sun drops and the heat with it',
  'the sun quits the rooftops',
  'shadow comes up the street wall to wall'
];

const lists = {
  'road.day': dayLines,
  'road.night': nightLines,
  'town.day': townDayLines,
  'town.night': townNightLines
};

// A shuffle bag per list rather than plain random: every line shows once before any repeats,
// which keeps a short list from feeling shorter than it is.
const bags = {};
let lastShown = null;

const shuffle = list => {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

const refill = key => {
  const bag = shuffle(lists[key]);

  // Lines are drawn off the end. If the refill would hand back the line that just showed,
  // swap it to the front so it comes last instead.
  if (bag.length > 1 && bag[bag.length - 1] === lastShown) {
    [bag[0], bag[bag.length - 1]] = [bag[bag.length - 1], bag[0]];
  }

  bags[key] = bag;
};

// place is 'road' or 'town'. Each keeps its own bag, so walking out of a town does not
// hand the road a half-empty one.
const ambient = (isNight, place = 'road') => {
  const key = `${place}.${isNight ? 'night' : 'day'}`;
  if (!bags[key] || bags[key].length === 0) refill(key);

  lastShown = bags[key].pop();
  return lastShown;
};

// Called when a run restarts, so a new game doesn't inherit a half-empty bag.
const resetAmbient = () => {
  Object.keys(bags).forEach(key => delete bags[key]);
  lastShown = null;
};

export { ambient as default, resetAmbient };
